import { usePrevious } from '@react-hookz/web';
import type { DependsOnType, DynamicPath } from '@unifyapps/defs/types/page';
import { isEqual } from 'lodash';
import { useMemo, useRef } from 'react';
import { ComputeContextKeys } from '../../../hooks/computeContext/const';
import {
  type UseGetNoCodeComputeDataType,
  useGetNoCodeComputedData,
} from '../../../hooks/computeContext/useGetNoCodeComputedData';
import { InterfaceModes } from '../../../stores/InterfaceStore';
import { useDSCMode, useDSCUseNonReactiveComputeContext } from '../context/selectors';
import { addEnvVariablesRegex } from '../utils/envVariablesInDataSource';

const getComputedData = <T>({
  data,
  context,
  envVariablesUsed,
  getNoCodeComputedData,
  dynamicPaths,
}: {
  data: Record<string, unknown>;
  context: Record<string, unknown>;
  envVariablesUsed?: string[];
  getNoCodeComputedData: UseGetNoCodeComputeDataType['getNoCodeComputedData'];
  dynamicPaths?: DynamicPath[];
}): T => {
  const computedData = getNoCodeComputedData<T>(data, context);

  if (envVariablesUsed) {
    return addEnvVariablesRegex({
      data,
      computedData: computedData as Record<string, unknown>,
      dynamicPaths,
    });
  }

  return computedData;
};

export function useComputeDataSourcePayload<T>({
  data,
  refetchOnPayloadChange = true,
  dataSourceId,
  key,
  dependsOn,
  dynamicPaths,
}: {
  data: Record<string, unknown>;
  refetchOnPayloadChange?: boolean;
  dataSourceId: string;
  key: string;
  dependsOn?: DependsOnType[];
  dynamicPaths?: DynamicPath[];
}) {
  const envVariablesUsed = useMemo(
    () => dependsOn?.find(({ id }) => id === ComputeContextKeys.ENV_VARIABLES)?.p,
    [dependsOn],
  );

  const { getNoCodeComputedData } = useGetNoCodeComputedData();
  const useNonReactiveComputeContext = useDSCUseNonReactiveComputeContext();

  const { context } = useNonReactiveComputeContext({
    entityId: dataSourceId,
    key,
  });
  const computedPayload = useRef<T | null>(null);
  const prevData = usePrevious(data);
  const mode = useDSCMode();

  // We are assuming that data doesn't change in runner mode
  const isDataChanged = useMemo(() => {
    // in runner mode, the payload doesn't change
    if (mode === InterfaceModes.RUNNER) return false;
    return !isEqual(prevData, data);
  }, [data, mode, prevData]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: for data we want to depend on isDataChanged
  return useMemo(() => {
    if (refetchOnPayloadChange) {
      computedPayload.current = getComputedData<T>({
        data,
        context,
        envVariablesUsed,
        getNoCodeComputedData,
        dynamicPaths,
      });
    }

    if (computedPayload.current === null) {
      const computedData = getComputedData<T>({
        data,
        context,
        envVariablesUsed,
        getNoCodeComputedData,
        dynamicPaths,
      });
      computedPayload.current = computedData;
    }
    return computedPayload.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- for data we want to depend on isDataChanged
  }, [
    context,
    dataSourceId,
    isDataChanged,
    refetchOnPayloadChange,
    dynamicPaths,
    envVariablesUsed,
  ]);
}
